// Utility functions for UI components

export const generateMSNAvatar = (seed: string): string => {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = seed.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = seed === 'Ghost' ? 110 : Math.abs(hash % 360);
  
  let svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16">
    <defs>
      <linearGradient id="bg" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stop-color="#051010" />
        <stop offset="60%" stop-color="#0b1e2e" />
        <stop offset="100%" stop-color="#0a312d" />
      </linearGradient>
      <linearGradient id="grid" x1="0%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stop-color="hsl(${hue}, 100%, 50%)" stop-opacity="0" />
        <stop offset="100%" stop-color="hsl(${hue}, 100%, 50%)" stop-opacity="0.6" />
      </linearGradient>
      <filter id="glow" x="-30%" y="-30%" width="160%" height="160%">
        <feDropShadow dx="0" dy="1" stdDeviation="1.5" flood-color="hsl(${hue}, 100%, 50%)" flood-opacity="0.6"/>
      </filter>
    </defs>
    <rect width="16" height="16" fill="url(#bg)" />
  `;

  for(let i=0; i<10; i++) {
    const sx = Math.abs((hash * (i+1) * 17) % 16);
    const sy = Math.abs((hash * (i+2) * 23) % 10);
    const size = i % 3 === 0 ? 1 : 0.5;
    svg += `<rect x="${sx}" y="${sy}" width="${size}" height="${size}" fill="#ffffff" opacity="${0.3 + (i%5)/10}" />`;
  }

  svg += `<path d="M0,11 L16,11 M0,13 L16,13 M0,15 L16,15 M8,10 L8,16 M6,10 L2,16 M10,10 L14,16 M4,10 L-4,16 M12,10 L20,16" stroke="url(#grid)" stroke-width="0.2" />`;

  const shape = [
    "      XXXX      ",
    "    XXXXXXXX    ",
    "   XXXXXXXXXX   ",
    "   XXXXXXXXXX   ",
    "   XXXXXXXXXX   ",
    "    XXXXXXXX    ",
    "      XXXX      ",
    "                ",
    "      XXXX      ",
    "    XXXXXXXX    ",
    "  XXXXXXXXXXXX  ",
    " XXXXXXXXXXXXXX ",
    " XXXXXXXXXXXXXX ",
    "XXXXXXXXXXXXXXXX",
    "XXXXXXXXXXXXXXXX"
  ];

  svg += `<g filter="url(#glow)">`;
  
  for (let y = 0; y < shape.length; y++) {
    for (let x = 0; x < shape[y].length; x++) {
      if (shape[y][x] === 'X') {
        const isHead = y < 7;
        const dx = x - 5;
        const dy = y - (isHead ? 2 : 9);
        const dist = Math.sqrt(dx*dx + dy*dy);
        
        const lightness = Math.max(30, 85 - dist * 6);
        const saturation = Math.min(100, 50 + (dist * 5));
        const color = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
        
        svg += `<rect x="${x}" y="${y+1}" width="1" height="1" fill="${color}" />`;
        svg += `<rect x="${x}" y="${y+1}" width="1" height="0.15" fill="#ffffff" opacity="0.5" />`;
        svg += `<rect x="${x}" y="${y+1}" width="0.15" height="1" fill="#ffffff" opacity="0.5" />`;
        svg += `<rect x="${x}" y="${y+1.85}" width="1" height="0.15" fill="#000000" opacity="0.4" />`;
        svg += `<rect x="${x+0.85}" y="${y+1}" width="0.15" height="1" fill="#000000" opacity="0.4" />`;
      }
    }
  }

  svg += `</g></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
};

interface ScrambleQueueItem {
    from: string;
    to: string;
    start: number;
    end: number;
    char?: string;
}

export class TextScramble {
  el: HTMLElement;
  chars: string;
  queue: ScrambleQueueItem[];
  frame: number;
  frameRequest: number;
  resolve: ((value: void | PromiseLike<void>) => void) | null = null;

  constructor(el: HTMLElement) {
    this.el = el;
    this.chars = '!<>-_\/[]{}—=+*^?#________';
    this.queue = [];
    this.frame = 0;
    this.frameRequest = 0;
    this.update = this.update.bind(this);
  }

  setText(newText: string) {
    const oldText = this.el.innerText;
    const length = Math.max(oldText.length, newText.length);
    const promise = new Promise<void>((resolve) => (this.resolve = resolve));
    this.queue = [];
    for (let i = 0; i < length; i++) {
      const from = oldText[i] || '';
      const to = newText[i] || '';
      const start = Math.floor(Math.random() * 40);
      const end = start + Math.floor(Math.random() * 40);
      this.queue.push({ from, to, start, end });
    }
    cancelAnimationFrame(this.frameRequest);
    this.frame = 0;
    this.update();
    return promise;
  }

  update() {
    let output = '';
    let complete = 0;
    for (let i = 0, n = this.queue.length; i < n; i++) {
      let { from, to, start, end, char } = this.queue[i];
      if (this.frame >= end) {
        complete++;
        output += to;
      } else if (this.frame >= start) {
        if (!char || Math.random() < 0.28) {
          char = this.chars[Math.floor(Math.random() * this.chars.length)];
          this.queue[i].char = char;
        }
        output += `<span class="scramble-char">${char}</span>`;
      } else {
        output += from;
      }
    }
    this.el.innerHTML = output;
    if (complete === this.queue.length) {
      if (this.resolve) this.resolve();
    } else {
      this.frameRequest = requestAnimationFrame(this.update);
      this.frame++;
    }
  }
}
